import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Heart, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';

function GalleryPage() {
  const { state, actions } = useApp();
  const navigate = useNavigate();
  const [allPhotos, setAllPhotos] = useState([]);

  useEffect(() => {
    if (state.spots.length === 0) {
      actions.fetchSpots();
    }
  }, [state.spots.length, actions]);

  // Create array of all photos with their spot info when spots change
  useEffect(() => {
    if (state.spots.length > 0) {
      const photos = [];
      state.spots.forEach(spot => {
        if (spot.photos && Array.isArray(spot.photos)) {
          spot.photos.forEach((photo, photoIndex) => {
            photos.push({
              id: `${spot.id}-${photoIndex}`,
              url: photo.url || photo,
              metadata: photo.metadata || {},
              spot: spot,
              photoIndex: photoIndex
            });
          });
        }
      });
      
      // Shuffle the photos randomly for discovery
      const shuffledPhotos = photos.sort(() => Math.random() - 0.5);
      setAllPhotos(shuffledPhotos);
    }
  }, [state.spots]);

  const handlePhotoClick = (photo) => {
    // Navigate to the spot page with details modal
    navigate(`/spot/${photo.spot.id}?details=true`);
  };

  const handleSpotClick = (spot) => {
    // Navigate to main page and select the spot
    actions.setSelectedSpot(spot);
    navigate('/');
  };

  const handleBackToMap = () => {
    navigate('/');
  };

  const handleOpenGallery = () => {
    // Navigate to main page and open the TikTok gallery
    navigate('/');
    setTimeout(() => {
      actions.setShowTikTokGallery(true);
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              onClick={handleBackToMap}
              className="flex items-center space-x-2"
            >
              <ArrowLeft size={20} />
              <span>Retour à la carte</span>
            </Button>
            <h1 className="text-2xl font-bold text-gray-800">Galerie</h1>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              onClick={handleOpenGallery}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700"
            >
              <Eye size={16} />
              <span>Ouvrir la Galerie TikTok</span>
            </Button>
            <div className="text-sm text-gray-600">
              {allPhotos.length} photos de {state.spots.length} spots
            </div>
          </div>
        </div>
      </div>

      {/* Simple Gallery with button to open real gallery */}
      <div className="max-w-7xl mx-auto p-6">
        {allPhotos.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500">Chargement de la galerie...</div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Découvrez tous nos spots en photos</h2>
              <p className="text-gray-600 mb-8">Explorez {allPhotos.length} photos magnifiques de {state.spots.length} spots uniques</p>
              
              <Button
                onClick={handleOpenGallery}
                size="lg"
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 mx-auto text-lg px-8 py-4"
              >
                <Eye size={20} />
                <span>Ouvrir la Galerie Interactive</span>
              </Button>
            </div>
            
            {/* Preview grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-8">
              {allPhotos.slice(0, 12).map((photo) => (
                <div
                  key={photo.id}
                  className="aspect-square cursor-pointer group overflow-hidden rounded-lg"
                  onClick={handleOpenGallery}
                >
                  <img
                    src={photo.url}
                    alt={photo.spot.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                </div>
              ))}
            </div>
            
            {allPhotos.length > 12 && (
              <div className="mt-6 text-gray-500">
                Et {allPhotos.length - 12} photos de plus à découvrir...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default GalleryPage;